import classNames from 'classnames'
import { useEffect } from 'react'

import { IProductCard } from '@src/components/product-card/product-card.types'
import BATCHED_EVENT_LIMIT from '@src/constants/batchedEventLimit'
import { SelectedFilter } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import { LoaderWrapper } from '@src/quince-components/src'
import useDeviceInfo from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { batchedAnalytics } from '@src/quince-site-common/src/utils/batched-analytics'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import { getProductDataForBatchedAnalytics } from '@src/quince-site-common/src/utils/getProductDataForBatchedAnalytics'
import { getProductTagsDataForBatchedAnalytics } from '@src/quince-site-common/src/utils/getProductTagsDataForBatchedAnalytics'
import { isBrowser } from '@src/quince-site-common/src/utils/windowUtil'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { getTrackingMetaData, PRODUCT_VIEW_TRACKER } from './searchResults.constants'
import styles from './searchResults.module.scss'
import { SearchResultsContent } from './SearchResultsContent'

export interface SearchResultsLegacyProps {
  searchQuery: string
  isLayoutLoading: boolean
  productCards: IProductCard[]
  intersectRef: (el: HTMLElement | null) => void
  isMoreProductsLoading: boolean
  totalProducts: number
  onResetFilters: () => void
  selectedFilters: SelectedFilter
  showDesktopFilterWithSidebar: boolean
}

export const SearchResultsLegacy = ({
  searchQuery,
  isLayoutLoading,
  productCards,
  intersectRef,
  isMoreProductsLoading,
  onResetFilters,
  selectedFilters,
  showDesktopFilterWithSidebar,
}: SearchResultsLegacyProps) => {
  const deviceInfo = useDeviceInfo()
  const url = isBrowser() && new URL(window.location.href)
  const hideSearchHeader = url && url.searchParams.get('hide_header') == 'true'

  const maxWidth = getMaxWidthInPx({
    maxWidthMobileInRem: styles.productCardWidthMobile ?? '',
    maxWidthDesktopInRem: styles.productCardWidth ?? '',
    deviceType: deviceInfo.deviceType,
  })

  useEffect(() => {
    batchedAnalytics.initializeBatchedAnalyticsEvent({
      tracker: PRODUCT_VIEW_TRACKER,
      eventLimit: BATCHED_EVENT_LIMIT[TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW],
      debouncePeriod: 1000,
    })
    batchedAnalytics.trackBatchedAnalytics({
      id: `internalAnalytics-${PRODUCT_VIEW_TRACKER}`,
      tracker: PRODUCT_VIEW_TRACKER,
      callback: ({ events }) => {
        const productList = events.map(event => getProductDataForBatchedAnalytics(event))
        const tagsCount = getProductTagsDataForBatchedAnalytics(productList)
        trackEvent(TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW, {
          productList: productList,
          tagsCount: tagsCount,
          ...getTrackingMetaData().context,
        })
      },
    })
  }, [])

  return (
    <div
      className={classNames(`${styles.searchResultsContainer} ${styles.searchResultsContainer__newCLPFilters ?? ''}`, {
        [styles.searchResultsContainer__paddingTop ?? '']: hideSearchHeader,
        [styles.searchResultsContainer__centered ?? '']: !productCards.length && !isLayoutLoading,
      })}
    >
      {searchQuery && (
        <LoaderWrapper showLoader={isLayoutLoading} loadWrapperClassName={styles.searchResultsContainer__loaderWrapper}>
          <SearchResultsContent
            productCards={productCards}
            showLoader={isLayoutLoading}
            maxWidth={maxWidth}
            searchQuery={searchQuery}
            onResetFilters={onResetFilters}
            selectedFilters={selectedFilters}
            showDesktopFilterWithSidebar={showDesktopFilterWithSidebar}
          />

          <div id="boundary_bottom" ref={intersectRef} />
          <LoaderWrapper
            showLoader={isMoreProductsLoading}
            // eslint-disable-next-line i18next/no-literal-string
            minHeight={'20rem'}
          >
            <div />
          </LoaderWrapper>
        </LoaderWrapper>
      )}
    </div>
  )
}
